/**
 * Composite Analyzer: EMA Proximity Intelligence (setup scope)
 *
 * Goes beyond the existing "with-stack / against-stack" classification used
 * across the bar-context analyzers. Asks the deeper questions:
 *
 *   1. Is THIS setup specifically a pullback edge? If so, to WHICH EMA?
 *      A 21-EMA pullback edge looks different from a 50-EMA pullback edge.
 *      Same setup name, different entry levels → different edges. Naming
 *      the level the setup actually wins on lets the trader gate entries.
 *
 *   2. Are extended entries (far from any EMA, "chasing") leaking edge?
 *      Same setup, entry-after-trigger vs entry-at-trigger has a known
 *      WR gap in real trading; this measures it on the trader's data
 *      instead of relying on platitudes.
 *
 * Why "composite" — this analyzer reads two-plus fields from barAnalysis +
 * the trade itself together, instead of looking at one signal in isolation.
 * Existing analyzers only ask "is the stack aligned?"; this asks "where
 * RELATIVE to the stack did you enter, and does it matter?"
 *
 * Reads:
 *   trade.barAnalysis.emasAtEntry — { 5: price, 9: price, 21: price, 50: price, 200: price }
 *   trade.entry      — entry price (already on the trade)
 *   trade.direction  — 'long' | 'short'
 *   trade.netPnl     — for win/loss bucketing (must be enriched by scope)
 *   trade.stopLoss / trade.stopLossPoints — used to normalize distance to "stop fractions"
 *
 * Trades without emasAtEntry OR without a stop reference are skipped.
 * Backfill (Settings → Charts → Run backfill) is the prerequisite for full
 * signal here.
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../../types.js';
import { normalizeDirection } from '../../../barAnalysis/counterSignals.js';

export const key = 'emaProximityIntelligence';
export const tier = 'free';

// Sample-size guards — pullback / chase edges are real but they need real
// counts to separate signal from variance.
const MIN_TRADES_PER_BUCKET = 4;
// Pullback bucket thresholds, expressed as fraction of the trade's stop.
// Why "stops" not "ticks" — ticks vary by instrument; stops are normalized
// to the trade's own risk so an MNQ pullback and an MES pullback both fall
// in the same bucket.
const PULLBACK_AT      = 0.20;   // Within 20% of stop = "at" the EMA
const PULLBACK_NEAR    = 0.50;   // Within 50% of stop = pullback zone
const CHASE_THRESHOLD  = 1.00;   // Beyond 1.0 stops from nearest EMA = extended/chasing
const PROFILE_MIN_GAP  = 0.15;   // 15pp WR gap for an EMA period to "stand out"
const CHASE_MIN_GAP    = 0.20;   // Chasing has to lose by 20pp to call it out

const fmtPct = (n) => `${Math.round(n * 100)}%`;

/**
 * Compute the trade's stop in price-points. Prefers `stopLossPoints` (already
 * in points), falls back to deriving from `stopLoss` (an absolute price).
 * Returns null if neither is usable — those trades are skipped because we
 * have no sensible reference distance.
 */
const stopInPoints = (trade) => {
  const direct = parseFloat(trade?.stopLossPoints);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const sl = parseFloat(trade?.stopLoss);
  const en = parseFloat(trade?.entry);
  if (!Number.isFinite(sl) || !Number.isFinite(en)) return null;
  const dist = Math.abs(en - sl);
  return dist > 0 ? dist : null;
};

/**
 * For one trade, walk every EMA in `emasAtEntry` and return:
 *   - closestPeriod       — which EMA (5, 9, 21, 50, 200) the entry was nearest
 *   - closestDistStops    — normalized distance to that EMA (in stop fractions)
 *   - extended            — true if even the nearest EMA is ≥ CHASE_THRESHOLD stops away
 *
 * Returns null when emasAtEntry is missing/empty or the trade has no usable
 * stop reference.
 */
export const classifyEmaProximity = (trade) => {
  const emas = trade?.barAnalysis?.emasAtEntry;
  if (!emas || typeof emas !== 'object') return null;
  const entry = parseFloat(trade?.entry);
  if (!Number.isFinite(entry)) return null;
  const stopPts = stopInPoints(trade);
  if (!stopPts) return null;

  let closestPeriod = null;
  let closestDistStops = Infinity;
  for (const [periodKey, raw] of Object.entries(emas)) {
    const ema = parseFloat(raw);
    if (!Number.isFinite(ema)) continue;
    const period = parseInt(periodKey, 10);
    if (!Number.isFinite(period) || period < 1) continue;
    const distStops = Math.abs(entry - ema) / stopPts;
    if (distStops < closestDistStops) {
      closestDistStops = distStops;
      closestPeriod = period;
    }
  }
  if (closestPeriod == null) return null;
  return {
    closestPeriod,
    closestDistStops,
    extended: closestDistStops >= CHASE_THRESHOLD,
  };
};

// ── Pattern 1: Per-EMA pullback edge ──────────────────────────────────────
//
// Identifies the setup's "go-to" EMA pullback level — the one specific EMA
// (e.g., 21) that, when entry is at/near it, drives the win rate. This is
// the "name your edge" insight the trader can't easily extract themselves
// because their setup name is the same regardless of which EMA they're
// pulling back to.

const detectPullbackEdge = (setupName, trades, direction = null, tagDirection = false) => {
  // Direction-match guard (v7.6.x). EMA proximity is a directional concept:
  // a "21-EMA pullback" for longs means entry on the way DOWN to the 21
  // (riding the uptrend), but for shorts means entry on the way UP to the
  // 21 (riding the downtrend). Same proximity metric, opposite trades.
  // When the caller passes a `direction`, filter trades so the WR cohort
  // applies. When omitted (legacy callers), behavior is unchanged.
  const filtered = direction
    ? trades.filter(t => normalizeDirection(t?.direction) === direction)
    : trades;

  // Bucket pullback-zone trades by closest EMA period.
  const byPeriod = new Map();   // period → trades[] (only PULLBACK_NEAR or closer)
  let total = 0;
  let extendedCount = 0;
  for (const t of filtered) {
    const p = classifyEmaProximity(t);
    if (!p) continue;
    total++;
    if (p.extended) { extendedCount++; continue; }
    if (p.closestDistStops > PULLBACK_NEAR) continue;
    if (!byPeriod.has(p.closestPeriod)) byPeriod.set(p.closestPeriod, []);
    byPeriod.get(p.closestPeriod).push(t);
  }
  if (byPeriod.size === 0) return null;

  // Compute WR per period bucket.
  const stats = [];
  for (const [period, group] of byPeriod) {
    if (group.length < MIN_TRADES_PER_BUCKET) continue;
    const wins = group.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length;
    const wr = wins / group.length;
    stats.push({ period, count: group.length, wins, wr });
  }
  if (stats.length === 0) return null;

  // Pick the strongest period (highest WR, then highest count as tiebreak).
  stats.sort((a, b) => (b.wr - a.wr) || (b.count - a.count));
  const best = stats[0];
  if (best.wr < 0.55) return null;  // Even a "best" pullback level needs to actually win.

  // Compare against the average WR across OTHER periods. If this period only
  // wins as much as the others, there's no specific edge to call out.
  const others = stats.slice(1);
  let othersAvgWr = null;
  if (others.length > 0) {
    const totalCount = others.reduce((s, x) => s + x.count, 0);
    const weightedWins = others.reduce((s, x) => s + x.wins, 0);
    othersAvgWr = totalCount > 0 ? weightedWins / totalCount : null;
    if (othersAvgWr != null && (best.wr - othersAvgWr) < PROFILE_MIN_GAP) return null;
  }

  // Direction tag — only when the analyze() call signals both directions
  // were considered (so the user knows which cohort drove this signal).
  // For single-direction setups, the tag is omitted to keep titles clean.
  const dirTag = (tagDirection && direction) ? ` (${direction.toUpperCase()})` : '';
  const dirIdPart = direction ? `${direction}:` : '';

  return makeObservation({
    id: `${key}:${setupName}:${dirIdPart}pullback-edge:${best.period}`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.SETUP,
    title: `"${setupName}"${dirTag} is a ${best.period}-EMA pullback edge (${fmtPct(best.wr)} WR)`,
    detail: othersAvgWr != null
      ? `${best.wins}/${best.count} winners when entry is within ${PULLBACK_NEAR.toFixed(1)} stops of the ${best.period} EMA. Other-EMA pullbacks: ${fmtPct(othersAvgWr)} WR. The level is doing the work, not the setup name.`
      : `${best.wins}/${best.count} winners when entry is within ${PULLBACK_NEAR.toFixed(1)} stops of the ${best.period} EMA. This is the level the setup actually wins on.`,
    action: `Gate this setup: entry must be within ${PULLBACK_NEAR.toFixed(1)} stops of the ${best.period} EMA. Anything farther isn't this setup — it's a different trade.`,
    evidence: {
      bestPeriod: best.period,
      bestWr: best.wr,
      bestCount: best.count,
      othersAvgWr,
      perPeriod: stats,
      extendedCount,
      total,
    },
    confidence: 0.82,
    priority: 84,
  });
};

// ── Pattern 2: Chase penalty (extended entries) ──────────────────────────
//
// Compares entries that were within the pullback zone vs entries that were
// extended (≥ CHASE_THRESHOLD stops away from the nearest EMA). Surfaces
// when chasing entries are demonstrably worse — the kind of trade where
// the trader knew they were late but pressed anyway.

const detectChasePenalty = (setupName, trades, direction = null, tagDirection = false) => {
  // Direction-match guard — see detectPullbackEdge for rationale.
  const filtered = direction
    ? trades.filter(t => normalizeDirection(t?.direction) === direction)
    : trades;

  const near = [];
  const extended = [];
  for (const t of filtered) {
    const p = classifyEmaProximity(t);
    if (!p) continue;
    if (p.extended) extended.push(t);
    else if (p.closestDistStops <= PULLBACK_NEAR) near.push(t);
  }
  if (near.length < MIN_TRADES_PER_BUCKET || extended.length < MIN_TRADES_PER_BUCKET) return null;

  const wr = (group) => group.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length / group.length;
  const nearWr = wr(near);
  const extendedWr = wr(extended);
  const gap = nearWr - extendedWr;
  if (gap < CHASE_MIN_GAP) return null;

  // Identify a representative worst-extended trade so the trader has a
  // concrete loss to look at, not just a stat.
  let worstExtended = null;
  let worstNet = Infinity;
  for (const t of extended) {
    if (Number.isFinite(t.netPnl) && t.netPnl < worstNet) {
      worstNet = t.netPnl;
      worstExtended = t;
    }
  }
  const jumpAction = worstExtended?.id
    ? [makeAction({
        type: ACTION_TYPE.JUMP_TO_TRADE,
        label: 'Show worst chase',
        payload: { tradeId: worstExtended.id, date: worstExtended.date || null },
        doneLabel: 'Jumping…',
      })]
    : null;

  const dirTag = (tagDirection && direction) ? ` (${direction.toUpperCase()})` : '';
  const dirIdPart = direction ? `${direction}:` : '';

  return makeObservation({
    id: `${key}:${setupName}:${dirIdPart}chase-penalty`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    title: `"${setupName}"${dirTag} extended entries lose ${fmtPct(gap)} more — the chase is real`,
    detail: `Within ${PULLBACK_NEAR.toFixed(1)} stops of an EMA: ${fmtPct(nearWr)} WR (${near.length} trades). Beyond ${CHASE_THRESHOLD.toFixed(1)} stops (extended): ${fmtPct(extendedWr)} WR (${extended.length} trades).`,
    action: `Add a "must be within ${PULLBACK_NEAR.toFixed(1)} stops of nearest EMA" rule. Late entries on this setup aren't the same trade — they bleed.`,
    actions: jumpAction,
    evidence: {
      nearCount: near.length,
      extendedCount: extended.length,
      nearWr,
      extendedWr,
      gap,
      worstTradeId: worstExtended?.id ?? null,
    },
    confidence: 0.8,
    priority: 80,
  });
};

// ── Public analyze() ─────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { setupName, setupTrades } = ctx || {};
  if (!setupName || !Array.isArray(setupTrades) || setupTrades.length === 0) return null;

  // Live-trade gate: pullback-edge and chase-penalty both bucket trades by
  // outcome (WR per EMA / WR per chase bucket). Open trades have unrealized
  // P&L which isn't a realized outcome — including them fires premature
  // "this setup is failing at extended entries" claims while the user is
  // still in the trade (panic-exit risk). Matches summarizePnl semantics.
  const closedSetupTrades = setupTrades.filter(t => t?.isOpen !== true);
  if (closedSetupTrades.length === 0) return null;

  // v7.6.x — direction-segregated analysis. EMA proximity is a directional
  // concept (a "21-EMA pullback" trades opposite directions in uptrends vs
  // downtrends, regardless of setup name). We split the setup's trade
  // cohort by direction and run both detectors per direction.
  //
  // - For direction-implicit setups (always long, or always short), only
  //   one cohort has enough trades to fire — that observation comes out
  //   cleanly without a direction tag.
  // - For direction-flexible setups (S/R, VWAP Bounce — traded both
  //   ways), both cohorts may fire, and each title carries an explicit
  //   "(LONG)" / "(SHORT)" tag so the trader can tell which cohort drove
  //   which observation. IDs are direction-scoped so the two don't
  //   collide in the observation key space.
  const longCohort  = closedSetupTrades.filter(t => normalizeDirection(t?.direction) === 'long');
  const shortCohort = closedSetupTrades.filter(t => normalizeDirection(t?.direction) === 'short');
  const bothDirections = longCohort.length > 0 && shortCohort.length > 0;

  const out = [];
  for (const direction of ['long', 'short']) {
    const cohort = direction === 'long' ? longCohort : shortCohort;
    if (cohort.length === 0) continue;
    const pullback = detectPullbackEdge(setupName, cohort, direction, bothDirections);
    if (pullback) out.push(pullback);
    const chase = detectChasePenalty(setupName, cohort, direction, bothDirections);
    if (chase) out.push(chase);
  }
  return out.length === 0 ? null : out;
};

// Exports for tests
export {
  stopInPoints,
  detectPullbackEdge,
  detectChasePenalty,
  MIN_TRADES_PER_BUCKET,
  PULLBACK_AT,
  PULLBACK_NEAR,
  CHASE_THRESHOLD,
  PROFILE_MIN_GAP,
  CHASE_MIN_GAP,
};
